import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
  Divider,
} from '@mui/material';
import { CalendarToday, Email as EmailIcon, Google, Lock, Login as LoginIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { Collapse } from '@mui/material';
import api from '@/services/api';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { loginStart, loginSuccess, loginFailure, clearError } from '@/store/authSlice';
import { authService } from '@/services/authService';
import { oauthService, OAuthConfig } from '@/services/oauthService';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

type FormData = yup.InferType<typeof schema>;

interface PlanInfo {
  name: string;
  price: number;
  period: string;
  description: string;
  popular?: boolean;
}

const planDetails: Record<string, PlanInfo> = {
  starter: {
    name: 'Starter',
    price: 0,
    period: 'forever',
    description: 'For just a few players',
  },
  family: {
    name: 'Family',
    price: 0,
    period: 'month',
    description: 'For active families with multiple players',
    popular: true,
  },
  team: {
    name: 'Team',
    price: 0,
    period: 'TBD',
    description: 'For teams and small clubs',
  },
  club: {
    name: 'Club',
    price: 0,
    period: 'TBD',
    description: 'For large clubs and organizations',
  },
};

const RegisterPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error } = useAppSelector((state) => state.auth);
  const [signupsEnabled, setSignupsEnabled] = useState<boolean | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [oauthConfig, setOauthConfig] = useState<OAuthConfig | null>(null);
  const [organizationName, setOrganizationName] = useState('');
  const [orgNameError, setOrgNameError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);

  const selectedTier = searchParams.get('tier') || 'starter';
  const selectedPlan = planDetails[selectedTier] || planDetails.starter;
  const oauthLoaded = oauthConfig !== null;
  const hasSocialProviders = !!oauthConfig?.google.enabled;

  useEffect(() => {
    const checkSignupStatus = async () => {
      try {
        const response = await api.get('/auth/signup-status');
        setSignupsEnabled(response.data.signupsEnabled);
      } catch {
        // Default to enabled if check fails
        setSignupsEnabled(true);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkSignupStatus();
  }, []);

  useEffect(() => {
    const loadOAuthConfig = async () => {
      try {
        const config = await oauthService.getConfig();
        setOauthConfig(config);
      } catch {
        setOauthConfig(null);
      }
    };
    loadOAuthConfig();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  React.useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit = async (data: FormData) => {
    const name = organizationName.trim();
    if (name.length < 2) {
      setOrgNameError('Organization name must be at least 2 characters');
      return;
    }
    try {
      dispatch(loginStart());
      const response = await authService.register({
        email: data.email,
        password: data.password,
        organizationName: name,
        selectedTier: selectedTier.toUpperCase(), // Pass selected tier to backend
      });
      dispatch(loginSuccess(response));
      
      // Redirect to dashboard with welcome message
      navigate('/dashboard?welcome=true');
    } catch (error: any) {
      let message = 'Registration failed';
      
      if (error.response?.data?.error) {
        const errorData = error.response.data.error;
        
        // Handle validation errors with specific field details
        if (errorData.code === 'VALIDATION_ERROR' && errorData.details) {
          const fieldErrors = errorData.details.map((detail: any) => detail.message).join('. ');
          message = fieldErrors;
        } else if (errorData.code === 'EMAIL_EXISTS') {
          message = 'This email is already registered. Please use a different email or sign in instead.';
        } else {
          message = errorData.message || message;
        }
      }
      
      dispatch(loginFailure(message));
    }
  };

  // Show loading while checking signup status
  if (checkingStatus) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Show beta message if signups are disabled
  if (signupsEnabled === false) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{
            m: 1,
            width: 56,
            height: 56,
            bgcolor: 'warning.main'
          }}>
            <Lock fontSize="large" />
          </Avatar>
          <Typography component="h1" variant="h4" gutterBottom>
            Player Central
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Alpha Access
          </Typography>

          <Card sx={{ mt: 3, width: '100%' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Player Central is currently in alpha
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Public registration is not available at this time.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                If you have an invitation link, please use that to join. Otherwise, check back soon as we expand access.
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Box textAlign="center">
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Button variant="contained" color="primary" fullWidth startIcon={<LoginIcon />}>
                    Sign In with Existing Account
                  </Button>
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{
          m: 1,
          width: 56,
          height: 56,
          bgcolor: '#10b981'
        }}>
          <CalendarToday fontSize="large" />
        </Avatar>
        <Typography component="h1" variant="h4" gutterBottom>
          Player Central
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Create your account
        </Typography>

        {/* Selected Plan Display */}
        <Card sx={{ mt: 2, width: '100%', bgcolor: 'primary.main', color: 'white' }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
              <Typography variant="h6" fontWeight="bold">
                {selectedPlan.name} Plan
              </Typography>
            </Box>
            <Box display="flex" alignItems="baseline" justifyContent="center" gap={1} mb={1}>
              <Typography variant="h4" fontWeight="bold">
                {selectedTier === 'starter' ? 'Free' : '$TBD'}
              </Typography>
              {selectedTier !== 'starter' && (
                <Typography variant="body1">
                  /{selectedPlan.period}
                </Typography>
              )}
            </Box>
            {selectedTier !== 'starter' && (
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                14-day free trial • Cancel anytime
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mt: 3, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="organizationName"
              label="Organization Name"
              autoComplete="organization"
              autoFocus
              value={organizationName}
              onChange={(e) => {
                setOrganizationName(e.target.value);
                setOrgNameError('');
              }}
              error={!!orgNameError}
              helperText={orgNameError || 'e.g., "Smith Family Soccer", "Eagles Soccer Club"'}
            />

            {hasSocialProviders && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                {oauthConfig?.google.enabled && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Google />}
                    onClick={() => {
                      const name = organizationName.trim();
                      if (name.length < 2) {
                        setOrgNameError('Please enter an organization name first');
                        return;
                      }
                      oauthService.loginWithGoogle({ organizationName: name });
                    }}
                    sx={{
                      borderColor: '#4285F4',
                      color: '#4285F4',
                      '&:hover': {
                        borderColor: '#357ABD',
                        backgroundColor: 'rgba(66, 133, 244, 0.04)',
                      },
                    }}
                  >
                    Sign up with Google
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => setShowEmailForm(!showEmailForm)}
                  sx={{
                    borderColor: showEmailForm ? 'primary.main' : 'grey.400',
                    color: showEmailForm ? 'primary.main' : 'text.primary',
                    '&:hover': {
                      borderColor: showEmailForm ? 'primary.dark' : 'grey.600',
                      backgroundColor: showEmailForm ? 'rgba(25, 118, 210, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  Sign up with Email
                </Button>
              </Box>
            )}

            <Collapse in={showEmailForm || (oauthLoaded && !hasSocialProviders)}>
              <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
                <TextField
                  {...register('email')}
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
                <TextField
                  {...register('password')}
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  error={!!errors.password}
                  helperText={errors.password?.message || 'At least 8 characters with uppercase, lowercase, and a number'}
                />
                <TextField
                  {...register('confirmPassword')}
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                />

                {selectedTier !== 'starter' && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Your {selectedPlan.name} plan will start with a 14-day free trial.
                    No payment required until your trial ends.
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                  startIcon={loading ? undefined : <PersonAddIcon />}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : selectedTier === 'starter' ? (
                    'Create Free Account'
                  ) : (
                    `Start ${selectedPlan.name} Trial`
                  )}
                </Button>
              </Box>
            </Collapse>

            <Divider sx={{ my: 2 }} />

            <Box textAlign="center">
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                  Already have an account? Sign In
                </Typography>
              </Link>
            </Box>

            {selectedTier !== 'starter' && (
              <Box textAlign="center" mt={2}>
                <Link to="/pricing" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="text.secondary">
                    Want to choose a different plan?
                  </Typography>
                </Link>
              </Box>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mt: 4, mb: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Join thousands of sports parents tracking their players' schedules
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default RegisterPage;
